import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import NewsService from '../../../service/news/News';

interface NewsItem {
  articleidx: number;
  bcode: string;
  userid: string;
  title: string;
  content: string;
  writetime: string;
  updatetime: string;
  branchcode: string;
  category: string;
  is_important: string;
  url: string;
  thumb: string | null;
}

function MainNews() {
  const navigate = useNavigate();
  const [newsList, setNewsList] = useState<NewsItem[]>([]);

  const isToday = (dateStr: string) => {
    const today = new Date();
    const d = new Date(dateStr);

    return (
      today.getFullYear() === d.getFullYear() &&
      today.getMonth() === d.getMonth() &&
      today.getDate() === d.getDate()
    );
  };

  const getNewsData = async () => {
    try {
      const result = await NewsService.list(7, 1);
      setNewsList(result.data.list);
    } catch (error) {
      console.error('Error fetching news data:', error);
    }
  };

  useEffect(() => {
    getNewsData();
  }, []);

  const dateFormat = (dateStr: string) => {
    const inputDate = new globalThis.Date(dateStr.replace(/-/g, '/')); // Safari 호환
    const now = new globalThis.Date();

    const diffMs = now.getTime() - inputDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 24) {
      return `${diffHours}시간 전`;
    } else if (diffDays < 7) {
      return `${diffDays}일 전`;
    } else {
      const yyyy = inputDate.getFullYear();
      const mm = String(inputDate.getMonth() + 1).padStart(2, '0');
      const dd = String(inputDate.getDate()).padStart(2, '0');
      return `${yyyy}.${mm}.${dd}`;
    }
  };

  return (
    <Container>
      <SectionTitle>
        경공매 뉴스
        <ViewMoreButton onClick={() => navigate('/news')}>
          전체보기
          <img src="/assets/images/arrow/arrow-right-gray-small.svg" alt="" />
        </ViewMoreButton>
      </SectionTitle>
      {newsList.length > 0 && (
        <Content>
          <Main
            onClick={() => {
              window.open(newsList[0].url, '_blank');
            }}
          >
            {newsList[0].thumb && <MainImg src={newsList[0].thumb} />}
            <Description>
              <MainTitle>
                {newsList[0].title}
                {isToday(newsList[0].writetime) && (
                  <img
                    src="/assets/images/icon/new.svg"
                    alt="NEW"
                    width={16}
                    height={16}
                  />
                )}
              </MainTitle>

              <NewsDate>{dateFormat(newsList[0].writetime)}</NewsDate>
            </Description>
          </Main>

          <NewsList>
            {newsList.slice(1).map((item, idx) => (
              <ListItem
                key={idx}
                onClick={() => {
                  window.open(item.url, '_blank');
                }}
              >
                {item?.thumb && <ListImg src={item.thumb} />}
                <ListDescription>
                  <Title>
                    {item.title}
                    {isToday(item.writetime) && (
                      <img
                        src="/assets/images/icon/new.svg"
                        alt=""
                        width={16}
                        height={16}
                      />
                    )}
                  </Title>
                  <NewsDate>{dateFormat(item.writetime)}</NewsDate>
                </ListDescription>
              </ListItem>
            ))}
          </NewsList>
        </Content>
      )}
    </Container>
  );
}

export default MainNews;

const Container = styled.div`
  display: flex;
  width: 100%;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  background: #fff;
`;

const SectionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  color: rgba(0, 12, 30, 0.8);
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

const ViewMoreButton = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  color: #4e5968;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const Main = styled.div`
  display: flex;
  padding-bottom: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const MainImg = styled.img`
  width: 100%;
  flex-shrink: 0;
  object-fit: contain;
  border-radius: 8px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  font-size: 16px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.32px;

  display: -webkit-box;
  -webkit-line-clamp: 2; // 최대 줄 수
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MainTitle = styled(Title)`
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.36px;
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const NewsDate = styled.div`
  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const NewsContent = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;
  white-space: pre-wrap;

  display: -webkit-box;
  -webkit-line-clamp: 3; // 최대 줄 수
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NewsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const ListItem = styled.div`
  display: flex;
  padding-bottom: 16px;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ListImg = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
`;

const ListDescription = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`;
